import clsx from 'clsx';
import moment from 'moment';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { FC, useEffect, useMemo, useState } from 'react';
import { LICENSE_PRICE_CONFIG } from '@/core/constants';
import { LicensesType, TLicensePrice, TPersonalLicense } from '@/core/infra';
import { useContextDispatch } from '@/components/context';
import { getInvoiceId } from '@/components/ConfirmBuyingLicense/service';
import { CryptoBuyForm } from '@/components/ConfirmBuyingLicense/CryptoBuyForm';
import { useBuyFreeHandler } from '@/components/ConfirmBuyingLicense/hooks/useBuyFreeHandler';
import { createCheckout, sendPromoCode } from '@/components/Checkout/service';
import { useAffise } from '@/src/utils/affise';
import { formatPrice, LocalStorageService } from '@/helper';
import s from './CheckoutPanel.module.scss';

type CheckoutPanelProps = {
  type: LicensesType;
  price: TLicensePrice;
  license?: TPersonalLicense;
  paymentMethods: any;
  setLoading: (value: ((prevState: boolean) => boolean) | boolean) => void;
};

const CheckoutPanel: FC<CheckoutPanelProps> = (props) => {
  const { type, price, license, paymentMethods, setLoading } = props;

  const {
    priceId,
    currency,
    name: licenseLogin = '',
    value: licenseValue = 0,
    description: licenseDescription = '',
  } = price;

  const router = useRouter();
  const { t } = useTranslation();
  const { sendAffiseEvent } = useAffise();
  const { notificationShowAction } = useContextDispatch();

  const [promoCode, setPromoCode] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [discount, setDiscount] = useState(0);
  const [cryptoFormRef, setCryptoFormRef] = useState<HTMLFormElement>(null);

  const handleBuyFree = useBuyFreeHandler(
    {
      currency,
      originalPrice: licenseValue,
      extension: !!license,
      licenseId: license?.licenseId,
    },
    promoCode
  );

  useEffect(() => {
    if (discount) {
      sendNewPromoCode();
    }
  }, [licenseValue]);

  const totalPrice = useMemo(() => {
    return licenseValue - discount;
  }, [licenseValue, discount]);

  const changePromoCode = (event) => {
    setPromoCode(event.target.value);
  };

  const sendNewPromoCode = async () => {
    try {
      const result = await sendPromoCode(promoCode, currency, licenseValue);
      setDiscount(licenseValue - result.price);
    } catch (e) {
      setDiscount(0);
      notificationShowAction({ text: t('wrong-promocode'), color: 'red' });
    }
  };

  const payWithCrypto = async () => {
    if (typeof priceId !== 'string') return;

    setLoading(true);

    const invoiceId = await getInvoiceId({
      licenseId: license?.licenseId,
      isExtension: !!license,
      priceId: Number(priceId),
      promoCode: promoCode || null,
    }).catch(() => setLoading(false));

    if (!invoiceId) {
      setLoading(false);
      return;
    }

    setInvoiceId(invoiceId);
    cryptoFormRef.submit();
  };

  const handleSubmit = () => {
    LocalStorageService.set(
      'license-buy-process-data',
      JSON.stringify({
        type,
        date: nextDate,
        action: !!license ? (license.licenseStatus === 'EXPIRED' ? 'renew' : 'extend') : 'buy',
      })
    );

    if (totalPrice === 0) {
      handleBuyFree();
      return;
    }

    if (paymentMethods.includes('COIN_PAYMENTS')) {
      payWithCrypto();
    }

    if (paymentMethods.includes('CLOUD_PAYMENTS')) {
      createCheckout({
        sendAffiseEvent,
        router,
        currency,
        licenseLogin,
        setLoading,
        total: totalPrice,
        promocode: promoCode,
        originalPrice: licenseValue,
        licenseId: license?.licenseId,
        isExtension: !!license,
      });
    }
  };

  const licenseDateTitle = useMemo(() => {
    if (!license) return t('license_date-active-title');

    if (new Date(license.expirationDate) > new Date()) {
      return t('license_date-extend-title');
    }

    return t('license_date-renew-title');
  }, [license]);

  const nextDate = useMemo(() => {
    if (!LICENSE_PRICE_CONFIG[price?.name]?.months) {
      return null;
    }

    const dateFrom = license?.licenseStatus !== 'EXPIRED' ? license?.expirationDate : undefined;

    return moment(dateFrom)
      .add(LICENSE_PRICE_CONFIG[price?.name]?.months, 'months')
      .format('YYYY-MM-DD');
  }, [license, price]);

  const dates = useMemo(() => {
    const format = 'DD MMM YYYY';

    if (!LICENSE_PRICE_CONFIG[price?.name]?.months) {
      return t('prices_subscription_TigerXLT');
    }

    if (license?.licenseStatus !== 'EXPIRED') {
      return `${t('personal-license_expiration-expired')} ${moment(nextDate).format(format)}`;
    }

    return `${moment().format(format)} - ${moment(nextDate).format(format)}`;
  }, [license, price, nextDate]);

  const buttonText = useMemo(() => {
    const priceText =
      totalPrice === 0 ? t(`license_submit-free`) : formatPrice(totalPrice, currency);

    let actionText = t('license_submit-buy');

    if (license) {
      if (license?.licenseStatus !== 'EXPIRED') {
        actionText = t('license_submit-extend');
      } else {
        actionText = t('license_submit-renew');
      }
    }

    return `${actionText} ${priceText}`;
  }, [totalPrice, currency, license]);

  return (
    <div className={s.root}>
      <CryptoBuyForm
        amountf={totalPrice}
        invoice={invoiceId}
        item_name={licenseLogin}
        item_desc={licenseDescription}
        ref={setCryptoFormRef}
      />

      <div className={s.datesTitle}>{licenseDateTitle}</div>
      <div className={s.dates}>{dates}</div>

      <div className={s.promoFormTitle}>{t('license_date-promo-title')}</div>
      <div className={s.promoForm}>
        <input
          value={promoCode}
          type="text"
          placeholder={t('checkout_form_promoCode')}
          className={s.promoInput}
          onChange={changePromoCode}
          id="9334"
        />
        <button type="button" className={s.promoBtn} onClick={sendNewPromoCode}>
          {t('checkout_form_promoCode_apply')}
        </button>
      </div>

      <div className={s.price}>
        {t('checkout_form_subTotal')}
        <div className={s.priceValue}>{formatPrice(licenseValue, currency)}</div>
      </div>

      <div className={s.price}>
        {t('checkout_form_promoDiscount')}
        <div className={s.priceValue}>
          {t(`${discount ? '- ' : ''} ${formatPrice(discount, currency)}`)}
        </div>
      </div>

      <div className={s.price}>
        {t('checkout_form_total')}
        <div className={s.priceValue}>
          {totalPrice === 0
            ? t('prices_welcome_price_tab1_amount')
            : formatPrice(totalPrice, currency)}
        </div>
      </div>

      <button className={s.submitBtn} onClick={handleSubmit}>
        {buttonText}
      </button>
    </div>
  );
};

export { CheckoutPanel };
